import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import { resolveHubupAssetUrl } from '../../api/hubup';
import {
  buildHubupNotificationSharePack,
  copyShareText,
  downloadShareImage,
  openFacebookShare,
  openLinkedInShare,
  openTwitterShare,
  openWhatsAppShare,
  tryNativeShare,
} from '../../utils/hubupNotificationSocialShare';

export function HubupXShareIcon({ className = '', size = 16 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      aria-hidden
      fill="currentColor"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const NETWORKS = [
  { id: 'x', label: 'X (Twitter)', Icon: HubupXShareIcon },
  { id: 'linkedin', label: 'LinkedIn', iconName: 'Linkedin' },
  { id: 'facebook', label: 'Facebook', iconName: 'Facebook' },
  { id: 'whatsapp', label: 'WhatsApp', iconName: 'MessageCircle' },
];

export default function HubupNotificationSocialShareModal({ message, onClose }) {
  const pack = useMemo(
    () => buildHubupNotificationSharePack(message, resolveHubupAssetUrl),
    [message],
  );
  const [text, setText] = useState(pack.defaultText);
  const [hint, setHint] = useState('');
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    setText(pack.defaultText);
  }, [message?.id, pack.defaultText]);

  const showHint = (msg) => {
    setHint(msg);
    setTimeout(() => setHint(''), 3500);
  };

  const handleNetwork = async (id) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) {
      showHint('Le texte du post est vide.');
      return;
    }
    if (id === 'x') {
      openTwitterShare(trimmed);
      return;
    }
    if (id === 'whatsapp') {
      openWhatsAppShare(trimmed);
      return;
    }
    if (id === 'linkedin') {
      const r = await openLinkedInShare(trimmed);
      showHint(
        r.copied
          ? 'Texte copié — collez-le dans le post si LinkedIn ne l’a pas prérempli.'
          : 'Ouvrez le post LinkedIn et collez le texte du champ ci-dessus.',
      );
      return;
    }
    if (id === 'facebook') {
      const r = await openFacebookShare(trimmed);
      showHint(
        r.copied
          ? 'Texte copié — collez-le dans la publication si besoin.'
          : 'Collez le texte du champ ci-dessus dans Facebook.',
      );
    }
  };

  const handleCopy = async () => {
    try {
      await copyShareText(text);
      showHint('Texte copié.');
    } catch {
      showHint('Copie impossible.');
    }
  };

  const handleNativeShare = async () => {
    try {
      const r = await tryNativeShare({
        title: pack.title,
        text,
        imageUrls: pack.imageUrls,
      });
      if (!r.ok) showHint('Partage natif non disponible sur cet appareil.');
    } catch {
      showHint('Partage annulé ou impossible.');
    }
  };

  const handleDownloadAll = async () => {
    if (pack.imageUrls.length === 0) return;
    setDownloading('all');
    try {
      for (let i = 0; i < pack.imageUrls.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await downloadShareImage(
          pack.imageUrls[i],
          `hubup-${pack.title || 'visuel'}-${i + 1}`.slice(0, 80),
        );
      }
      showHint('Visuels téléchargés.');
    } catch {
      showHint('Téléchargement impossible.');
    } finally {
      setDownloading(null);
    }
  };

  if (!message) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1200] p-4">
      <div
        className="bg-card rounded-xl shadow-elevation-3 w-full max-w-lg border border-border max-h-[92vh] flex flex-col"
        role="dialog"
        aria-labelledby="hubup-social-share-title"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3 shrink-0">
          <h2 id="hubup-social-share-title" className="text-sm font-semibold text-foreground pr-2">
            Partager la notification
          </h2>
          <button
            type="button"
            className="p-1.5 rounded hover:bg-muted transition-smooth shrink-0"
            aria-label="Fermer"
            onClick={onClose}
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {NETWORKS.map((n) => (
              <button
                key={n.id}
                type="button"
                className="flex flex-col items-center gap-1.5 rounded-lg border border-border px-2 py-3 text-xs font-medium hover:bg-muted/60 transition-colors"
                onClick={() => void handleNetwork(n.id)}
              >
                {n.Icon ? (
                  <n.Icon size={20} className="text-foreground" />
                ) : (
                  <Icon name={n.iconName} size={20} className="text-foreground" />
                )}
                {n.label}
              </button>
            ))}
          </div>

          <div>
            <label className="text-xs font-medium text-foreground mb-1 block">
              Texte du post
            </label>
            <textarea
              className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <div className="flex flex-wrap gap-2 mt-2">
              <Button
                type="button"
                size="xs"
                variant="outline"
                iconName="Copy"
                onClick={() => void handleCopy()}
              >
                Copier le texte
              </Button>
              {typeof navigator !== 'undefined' && navigator.share ? (
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  iconName="Share2"
                  onClick={() => void handleNativeShare()}
                >
                  Partager (appareil)
                </Button>
              ) : null}
            </div>
          </div>

          {pack.imageUrls.length > 0 ? (
            <div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-xs font-medium text-foreground">
                  Visuels ({pack.imageUrls.length})
                </span>
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  iconName="Download"
                  disabled={downloading === 'all'}
                  onClick={() => void handleDownloadAll()}
                >
                  {downloading === 'all' ? 'Téléchargement…' : 'Tout télécharger'}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {pack.imageUrls.map((src) => (
                  <div
                    key={src}
                    className="relative group w-20 h-20 rounded-md border border-border overflow-hidden bg-muted"
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    <a
                      href={src}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity text-white text-[10px] font-medium"
                    >
                      Ouvrir
                    </a>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {hint ? <p className="text-xs text-muted-foreground text-center">{hint}</p> : null}
        </div>
      </div>
    </div>
  );
}
